import { useRouter } from 'next/router'
import NextLink from 'next/link'
import { useEffect, useState } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Link } from '@wartek-id/link'
import { Spinner } from '@wartek-id/spinner'

import { Header } from 'app/KinerjaShared/components/Header'
import { usePelaksanaanPerilakuKerja } from 'app/KinerjaPenilikPegawaiPage/hooks/usePelaksanaanPerilakuKerja'
import usePenilikPeriodGuard from 'app/KinerjaPenilikPegawaiPage/hooks/usePenilikPeriodGuard'
import { PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'

import type { PerilakuKerjaAspek } from 'types/kinerja-penilik-pegawai'

const PelaksanaanPerilakuKerjaPage = () => {
  const router = useRouter()
  const [isPageLoading, setPageLoading] = useState(true)

  // Entry Point Guards
  const { isPeriodSuccess, pelaksanaanGuard } = usePenilikPeriodGuard()

  const { pelaksanaan, apiStatus } = usePelaksanaanPerilakuKerja()

  const perilakuKerja: PerilakuKerjaAspek[] = !!pelaksanaan
    ? JSON.parse(pelaksanaan.perilakuKerjaValues)
    : []

  useEffect(() => {
    if (isPeriodSuccess && !pelaksanaanGuard.isOpen) {
      router.replace(PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH)
    } else if (isPeriodSuccess && apiStatus === 'success') {
      setPageLoading(false)
    }
  }, [apiStatus, isPeriodSuccess, pelaksanaanGuard.isOpen, router])

  if (isPageLoading) {
    return (
      <div className="flex justify-center mt-10">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="pt-10 pb-32 mx-auto max-w-7xl">
      <Header
        title="Perilaku Kerja"
        className="mb-9 print:hidden"
        onBack={router.back}
      />

      <div className="flex mt-10 gap-7">
        <div className="flex-1 p-6 bg-white border rounded border-subdued">
          <Text variant="heading-lg">Daftar Perilaku Kerja pilihan Anda</Text>
          <Text className="mt-1">
            <div>
              Anda tidak perlu kumpulkan Bukti Dukung. Cukup laksanakan dalam
              keseharian Anda di luar sistem.
            </div>
            <NextLink
              href="/pengelolaan-kinerja/penilik/pegawai/pelaksanaan/perilaku-kerja/panduan"
              passHref
            >
              <Link className="font-bold">Cek Daftar Perilaku Kerja.</Link>
            </NextLink>
          </Text>

          {apiStatus === 'loading' && (
            <div className="flex justify-center mt-6">
              <Spinner />
            </div>
          )}

          {apiStatus === 'success' && (
            <div
              className="flex flex-col gap-6 mt-6"
              data-testid="perilaku-kerja"
            >
              {perilakuKerja.map((pk) => (
                <div
                  key={pk.aspek}
                  className="flex flex-col gap-6 p-6 bg-white border rounded border-subdued"
                >
                  <Text variant="heading-lg">{pk.aspek}</Text>

                  <div className="flex flex-col gap-1">
                    <Text color="subdued">Indikator</Text>
                    <Text variant="action">{pk.indikator}</Text>
                  </div>

                  <div className="flex flex-col gap-1">
                    <Text color="subdued">Perwujudan</Text>
                    <Text variant="action">{pk.ekspektasi}</Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-10 flex justify-center w-full p-4 bg-white border-t border-subdued">
        <NextLink href="/pengelolaan-kinerja/penilik/pegawai" passHref>
          <Button as="a">Ke halaman utama Pelaksanaan</Button>
        </NextLink>
      </div>
    </div>
  )
}

export default PelaksanaanPerilakuKerjaPage
